import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { PassId } from "../models/PassId";
import { deletePassId, upsertPassId } from "../repositories/PassIdsRepository";
import { PASS_IDS_QUERY_KEYS } from "./usePassIds";

interface UsePassIdMutationOptions {
	onSuccess?: (passId: PassId) => void;
	onError?: (error: Error) => void;
	onDeleteSuccess?: () => void;
}

export const usePassIdMutation = ({
	onSuccess,
	onError,
	onDeleteSuccess,
}: UsePassIdMutationOptions = {}) => {
	const queryClient = useQueryClient();

	const saveMutation = useMutation({
		mutationFn: upsertPassId,
		onSuccess: (passId) => {
			queryClient.invalidateQueries({ queryKey: PASS_IDS_QUERY_KEYS.all });
			onSuccess?.(passId);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	const deleteMutation = useMutation({
		mutationFn: deletePassId,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: PASS_IDS_QUERY_KEYS.all });
			onDeleteSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		savePassId: saveMutation.mutate,
		deletePassId: deleteMutation.mutate,
		isSaving: saveMutation.isPending,
		isDeleting: deleteMutation.isPending,
	};
};
